import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { memo, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import { Bell01Icon } from "@/components/Icons/Bell01Icon";
import { ChevronDownIcon } from "@/components/Icons/ChevronDownIcon";
import { MessageCircle01Icon } from "@/components/Icons/MessageCircle01Icon";
import { ProjectSwitcher } from "@/components/ProjectSwitcher/ProjectSwitcher";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { buttonVariants } from "@/design-system/Button";
import { Spinner } from "@/design-system/Spinner";
import { hasAdminProject } from "@/helpers/portfolio-access";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { twResolve } from "@/helpers/tw-resolve";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUserOptional } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useOnScroll } from "@/hooks/useOnScroll";
import { useSignalRHub, useSignalRSubscription } from "@/hooks/useSignalR";
import { useSlug } from "@/hooks/useSlug";
import { useNotificationQueries } from "@/queries/notifications/queryOptions";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { ActivityDot } from "../ActivityDot/ActivityDot";

interface Props {
  isInProject: boolean;
  onToggleMenu: () => void;
}
export const MobileMenuBar = memo(MobileMenuBarContent);
function MobileMenuBarContent({ isInProject, onToggleMenu }: Props): React.ReactNode {
  const { t } = useTranslation();
  const [projectSwitcher, projectSwitcherHandlers] = useBool(false);
  const [amountUnreadMessages, setAmountUnreadMessages] = useState<{
    [projectId: string]: number;
  }>({});
  const slug = useSlug({
    optional: true,
  });
  const [sessionUser, { isPlaceholderData }] = useSessionUserOptional({
    keepPreviousData: true,
  });
  const isAtTopOfPage = useIsAtTopOfMainContent();
  const selfAccountQueries = useSelfAccountQueries();
  const notificationQueries = useNotificationQueries();
  const { data: linkedProjects } = useQuery(selfAccountQueries.listLinkedProjects());
  const projectId = useProjectId();
  const isResident = sessionUser?.role.isResidentType;
  const { data: adminNotificationStats } = useQuery({
    ...notificationQueries.adminStatus({
      staleTime: 30 * 1000,
      refetchInterval: 60 * 1000,
    }),
    enabled: isInProject && sessionUser?.isAdmin,
  });
  const { data: residentNotificationStats } = useQuery({
    ...notificationQueries.residentStatus({
      staleTime: 30 * 1000,
      refetchInterval: 60 * 1000,
    }),
    enabled: isInProject && isResident,
  });
  const hasUnreadNotifications = isResident
    ? !!residentNotificationStats?.unreadNotifications
    : adminNotificationStats?.hasUnread;
  const { signalRConnection } = useSignalRHub("notification-hub", {
    query: `userId=${sessionUser?.id}`,
    disabled: !sessionUser,
  });
  const onNewChatMessage = useCallback(
    (
      ...args: [
        {
          unreadChats: number;
          fromConnectionStarted: boolean;
        },
      ]
    ) => {
      if (args[0].unreadChats > 0) {
        setAmountUnreadMessages((x) => ({
          ...x,
          [projectId]: args[0].unreadChats,
        }));
      } else {
        setAmountUnreadMessages((x) => ({
          ...x,
          [projectId]: 0,
        }));
      }
    },
    [projectId],
  );
  useSignalRSubscription(signalRConnection, SIGNALR_EVENTS.CHAT.UPDATE_PROJECT_CHAT_UNREAD_COUNT, onNewChatMessage);
  const canSwitchProjects = linkedProjects && linkedProjects.length > 1;
  const canSeePortfolio = hasAdminProject(linkedProjects);

  return (
    <header
      className={twJoin(
        "z-10 grid w-full grid-cols-5 items-center border-b px-space-md py-space-sm transition-all",
        isAtTopOfPage ? "border-b-transparent shadow-none" : "border-border-default shadow-raised",
      )}
    >
      <section className="flex items-center">
        <button
          type="button"
          aria-label={t("component.mobile-navigation.action.open-menu")}
          className={twResolve(
            buttonVariants({ variant: "ghostSecondary", size: "md", shape: "circular", type: "button" }),
            "h-auto px-0",
          )}
          onClick={onToggleMenu}
        >
          <UserAvatar image={sessionUser?.avatar} />
        </button>
      </section>
      <section className="col-span-3 flex min-w-0 justify-center">
        <ProjectName
          sessionUser={sessionUser}
          isPlaceholderData={isPlaceholderData}
          isInProject={isInProject}
          canSwitchProjects={canSwitchProjects}
          projectSwitcherHandlers={projectSwitcherHandlers}
        />
      </section>
      {isInProject && slug && (
        <nav className="flex justify-end">
          <Link
            to={routes.chats.list({
              slug,
            })}
            aria-label={t("component.mobile-navigation.link.chats")}
            className="flex size-control-xl items-center justify-center p-space-xs"
          >
            <span className="relative flex w-icon-inline">
              <MessageCircle01Icon />
              {amountUnreadMessages[projectId] > 0 && (
                <div className="absolute -right-0.5">
                  <ActivityDot />
                </div>
              )}
            </span>
          </Link>
          {sessionUser && (
            <Link
              to={
                sessionUser.role.isResidentType
                  ? routes.residentNotifications.list({ slug })
                  : routes.adminNotifications.list({ slug })
              }
              aria-label={t("component.mobile-navigation.link.notifications")}
              className="flex size-control-xl items-center justify-center p-space-xs"
            >
              <span className="relative flex w-icon-inline">
                <Bell01Icon />
                {hasUnreadNotifications && (
                  <div className="absolute -right-0.5">
                    <ActivityDot />
                  </div>
                )}
              </span>
            </Link>
          )}
        </nav>
      )}
      {createPortal(
        <div
          className={twJoin("absolute inset-0 z-20", projectSwitcher ? "pointer-events-auto" : "pointer-events-none")}
        >
          <AnimatePresence>
            {projectSwitcher ? (
              <motion.button
                aria-hidden
                className="absolute inset-0 bg-background-default-inverse/60"
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
                onClick={projectSwitcherHandlers.setFalse}
              />
            ) : null}
          </AnimatePresence>

          <div className="absolute inset-x-0 mx-auto max-w-lg">
            <ProjectSwitcher
              isOpen={projectSwitcher}
              onClose={projectSwitcherHandlers.setFalse}
              showRecentProjects
              showCurrentProject={!isInProject}
              showPortfolio={canSeePortfolio && isInProject}
            />
          </div>
        </div>,
        document.body,
      )}
    </header>
  );
}
function useIsAtTopOfMainContent() {
  const [isAtTopOfPage, setIsAtTopOfPage] = useState(false);

  useOnScroll(() => {
    const element = getMainElement();
    setIsAtTopOfPage(element!.scrollTop === 0);
  });

  return isAtTopOfPage;
}

function getMainElement() {
  return document.getElementById("main");
}

function ProjectName({
  sessionUser,
  isPlaceholderData,
  isInProject,
  canSwitchProjects,
  projectSwitcherHandlers,
}: {
  sessionUser: ReturnType<typeof useSessionUserOptional>[0];
  isPlaceholderData: boolean;
  isInProject: boolean;
  canSwitchProjects?: boolean;
  projectSwitcherHandlers: ReturnType<typeof useBool>[1];
}) {
  const { t } = useTranslation();

  if (!sessionUser) {
    // Need to render an element or the layout will break
    return <span />;
  }

  if (isPlaceholderData) {
    return (
      <span className="mx-auto inline-flex w-min">
        <Spinner size="md" />
      </span>
    );
  }

  if (isInProject) {
    if (canSwitchProjects) {
      return (
        <button
          type="button"
          className={twResolve(
            buttonVariants({ variant: "subtle", size: "xl", type: "button" }),
            "max-w-full gap-space-sm px-toggle-button-inline-sm text-text-brand-secondary hover:text-text-primary",
          )}
          onClick={projectSwitcherHandlers.toggle}
        >
          <span className="truncate text-body-emphasis">{sessionUser?.project.name}</span>
          <span className="mt-0.5">
            <ChevronDownIcon />
          </span>
        </button>
      );
    }

    return (
      <span className="truncate text-center text-body-emphasis text-text-brand-secondary">
        {sessionUser?.project.name}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={twResolve(
        buttonVariants({ variant: "subtle", size: "xl", type: "button" }),
        "max-w-full gap-space-sm px-toggle-button-inline-sm text-text-brand-secondary hover:text-text-primary",
      )}
      onClick={projectSwitcherHandlers.toggle}
    >
      <span className="truncate text-body">{t("page.portfolio.project-switcher")}</span>
      <span className="mt-0.5">
        <ChevronDownIcon />
      </span>
    </button>
  );
}
